import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import { vehicleNumberScheme } from '@sravni/cosago-react-library/lib/validationSchemes';
import { Space, Spinner, Typography, Button, Icon } from '@sravni/react-design-system';
import { RoundCheck } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';
import React from 'react';

import { useAppSelector } from 'shared/lib';

import type { TPledgeAutoFields } from 'entities/pledgeAutoForm';
import {
  selectIsCarInfoEmpty,
  selectIsCarInfoLoadedSuccessfully,
  selectIsCarInfoLoading,
  selectIsCarInfoLoadingError,
  selectIsCarInfoLoadingInitialSate,
} from 'entities/pledgeAutoForm';

import { useUpdateCarNumberField } from 'features/PledgeAutoForm/lib/useUpdateCarNumberField';
import { PledgeAutoFormTexts } from 'features/PledgeAutoForm/PledgeAutoForm.texts';

import styles from './CarNumber.module.scss';

const { Text } = Typography;

const StatusContainer = ({ children }: { children: React.ReactNode }) => (
  <Space
    align="center"
    className={styles.carNumberLoadStatus}
    size={4}
  >
    {children}
  </Space>
);

export const CarInfoLoadingStatus = () => {
  const isCarInfoLoading = useAppSelector(selectIsCarInfoLoading);
  const isLoadedDataEmpty = useAppSelector(selectIsCarInfoEmpty);
  const isLoadedSuccessfully = useAppSelector(selectIsCarInfoLoadedSuccessfully);
  const isInitial = useAppSelector(selectIsCarInfoLoadingInitialSate);
  const isError = useAppSelector(selectIsCarInfoLoadingError);
  const { runUpdateCarNumberOnBlur } = useUpdateCarNumberField('carNumber');
  const isMobile = useIsMobile();
  const { getValues } = useFormContext<TPledgeAutoFields>();

  const isLoadButtonDisabled = !vehicleNumberScheme().isValidSync(getValues('carNumber'));

  if (isInitial) {
    return (
      <StatusContainer>
        <Button
          variant="text"
          onClick={runUpdateCarNumberOnBlur}
          size={36}
          disabled={isLoadButtonDisabled}
        >
          {PledgeAutoFormTexts.loadCarInfo}
        </Button>
      </StatusContainer>
    );
  }

  if (isCarInfoLoading) {
    return (
      <StatusContainer>
        <Spinner size={20} />
      </StatusContainer>
    );
  }

  if (isLoadedSuccessfully) {
    return (
      <StatusContainer>
        <Icon
          icon={<RoundCheck />}
          color="blue"
        />
        {isMobile && (
          <Text
            size={14}
            className={styles.loadStatusText}
          >
            {PledgeAutoFormTexts.carInfoLoaded}
          </Text>
        )}
      </StatusContainer>
    );
  }

  if (isError) {
    return (
      <StatusContainer>
        <Text size={14}>Ошибка загрузки данных.</Text>
        <Button
          variant="text"
          onClick={runUpdateCarNumberOnBlur}
          size={36}
        >
          {PledgeAutoFormTexts.tryLoadCarInfoAgain}
        </Button>
      </StatusContainer>
    );
  }

  if (isLoadedDataEmpty) {
    return (
      <StatusContainer>
        <Text
          size={14}
          className={styles.loadStatusText}
        >
          {PledgeAutoFormTexts.carInfoNotLoaded}
        </Text>
      </StatusContainer>
    );
  }

  return null;
};
